/**
 * Authentication context and hook
 * Manages user session, authentication state, and scope checking
 */

import { HttpServerError, NetworkError, RequestTimeoutError, RetryExhaustedError } from "@eoln/resilience";
import { AuthClient } from "@eoln/sdk-auth";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { clearSession, getSession, hasSession, setSession } from "@/lib/session";

const AUTH_GRAPHQL_ENDPOINT = import.meta.env.VITE_AUTH_GRAPHQL_ENDPOINT || "https://auth-api.mono-dev.eoln.eu/graphql";

interface AuthInfo {
  scopes: string[];
  tenantId: string | null;
  expiresAt: string | null;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  apiKey: string | null;
  authInfo: AuthInfo | null;
  login: (apiKey: string) => Promise<void>;
  logout: () => void;
  client: AuthClient | null;
  hasScope: (scope: string) => boolean;
  isRestoringScopes: boolean;
  hasAnyScope: (scopes: string[]) => boolean;
  /**
   * Set when the stored key could not be CHECKED (auth unreachable or 5xx),
   * which is not the same as revoked: the session is kept and routes let
   * the user through with a notice instead of redirecting.
   */
  restoreError: "unavailable" | null;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(hasSession());
  const [apiKey, setApiKey] = useState<string | null>(getSession());
  const [authInfo, setAuthInfo] = useState<AuthInfo | null>(null);
  const [restoreError, setRestoreError] = useState<"unavailable" | null>(null);

  // Restore scopes after a full page load. login() is the only other place
  // authInfo is set, so before this effect a refresh on any scope-guarded
  // route left isAuthenticated=true (the key survives in sessionStorage) but
  // hasScope()=false forever -- ProtectedRoute then bounced a validly
  // logged-in user to /unauthorized on every browser refresh of /checkout or
  // /customers/new. A revoked key logs out rather than leaving a
  // half-authenticated session; an auth OUTAGE does not -- "could not check"
  // is recorded in restoreError and the key is kept.
  useEffect(() => {
    if (!apiKey || authInfo) {
      return;
    }
    let cancelled = false;
    const restore = async () => {
      try {
        const restoreClient = new AuthClient({ endpoint: AUTH_GRAPHQL_ENDPOINT, apiKey });
        const info = await restoreClient.getAuthInfoWithKey();
        if (cancelled) {
          return;
        }
        if (!info.isAuthenticated || !info.apiKey) {
          throw new Error("revoked");
        }
        setAuthInfo({
          scopes: info.apiKey.scopes ?? [],
          tenantId: null,
          expiresAt: info.apiKey.expiresAt ? String(info.apiKey.expiresAt) : null,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (
          error instanceof NetworkError ||
          error instanceof HttpServerError ||
          error instanceof RequestTimeoutError ||
          error instanceof RetryExhaustedError
        ) {
          // Could not check, which is not the same as revoked. Keep the
          // session; ProtectedRoute shows a notice instead of redirecting.
          //
          // RetryExhaustedError is the one that actually fires in production:
          // AuthClient retries three times by default and RetryManager rethrows
          // the last failure wrapped, so a real outage never reaches here as a
          // bare NetworkError. Without it this guard only covered the case
          // where retry was disabled. RequestTimeoutError is the other shape
          // an unreachable auth service takes -- it is deliberately NOT
          // retryable, so it arrives here unwrapped.
          setRestoreError("unavailable");
          return;
        }
        clearSession();
        setApiKey(null);
        setIsAuthenticated(false);
        setAuthInfo(null);
      }
    };
    void restore();
    return () => {
      cancelled = true;
    };
  }, [apiKey, authInfo]);

  // True while a stored session exists but its scopes are still being
  // restored -- ProtectedRoute must WAIT on this, not pre-judge. A restore
  // that could not complete is not "still restoring".
  const isRestoringScopes = isAuthenticated && apiKey !== null && authInfo === null && restoreError === null;

  const client = useMemo(
    () =>
      apiKey
        ? new AuthClient({
            endpoint: AUTH_GRAPHQL_ENDPOINT,
            apiKey,
          })
        : null,
    [apiKey],
  );

  const login = useCallback(async (newApiKey: string) => {
    // Validate API key format: sk_ + 64 hex characters
    const apiKeyPattern = /^sk_[0-9a-f]{64}$/;
    if (!apiKeyPattern.test(newApiKey)) {
      throw new Error("Invalid API key format. Expected: sk_ followed by 64 hex characters");
    }

    // Validate key with backend using getAuthInfoWithKey to get scopes
    const tempClient = new AuthClient({
      endpoint: AUTH_GRAPHQL_ENDPOINT,
      apiKey: newApiKey,
    });

    const info = await tempClient.getAuthInfoWithKey();
    if (!info.isAuthenticated || !info.apiKey) {
      throw new Error("API key is not valid or has been revoked");
    }

    // Get scopes from the API key
    const scopes = info.apiKey.scopes ?? [];

    // Check for payments scopes -- wildcard-aware, mirroring the auth
    // service's scopeMatches. This exact-match version was the FIRST gate the
    // admin E2E key (payments:*, no literal payments:x) hit: its logins threw
    // here, so every admin-authenticated spec bounced to /login before the
    // route guard was even consulted.
    const grantsPayments = (held: string): boolean =>
      held === "*" || held === "payments:*" || held.startsWith("payments:");
    const hasPaymentsAccess = scopes.some(grantsPayments);

    if (!hasPaymentsAccess) {
      throw new Error(
        "API key does not have payments access. Required: payments:read, payments:write, or payments:admin",
      );
    }

    // Store in session
    setSession(newApiKey);
    setRestoreError(null);
    setApiKey(newApiKey);
    setAuthInfo({
      scopes,
      tenantId: null, // Not available in current API response
      expiresAt: info.apiKey.expiresAt ? String(info.apiKey.expiresAt) : null,
    });
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setApiKey(null);
    setAuthInfo(null);
    setRestoreError(null);
    setIsAuthenticated(false);
  }, []);

  const hasScope = useCallback(
    (scope: string) => {
      if (!authInfo) {
        return false;
      }
      // Mirror the auth service's scopeMatches (srv/auth/middleware/auth.ts):
      // exact match, `ns:*` prefix wildcard, bare `*` global. The admin E2E
      // key holds `payments:*`, not the literal `payments:admin` -- exact
      // matching sent even ADMIN logins bouncing off the payments:admin
      // ProtectedRoute on /checkout, a gap invisible until today's rebuild
      // because the stale deployed bundle predated the scope gate.
      const matches = (held: string): boolean => {
        if (held === scope || held === "*") {
          return true;
        }
        return held.endsWith(":*") && scope.startsWith(held.slice(0, -1));
      };
      if (authInfo.scopes.some(matches)) {
        return true;
      }
      // payments:admin has all payments permissions (UI parity with the
      // pre-wildcard behavior); payments:write includes read.
      if (scope.startsWith("payments:") && authInfo.scopes.includes("payments:admin")) {
        return true;
      }
      return scope === "payments:read" && authInfo.scopes.includes("payments:write");
    },
    [authInfo],
  );

  const hasAnyScope = useCallback(
    (scopes: string[]) => {
      return scopes.some((scope) => hasScope(scope));
    },
    [hasScope],
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        apiKey,
        authInfo,
        login,
        logout,
        client,
        hasScope,
        hasAnyScope,
        isRestoringScopes,
        restoreError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
